import { z } from 'zod';
import type { ParseResult } from '../voyage/schemas.js';
import {
  optionalDay,
  optionalParagraph,
  optionalText,
  optionalUuid,
  parsed,
  requiredParagraph,
  requiredText,
  typed,
} from './schema.js';

/**
 * Dry dock projesi ve Pre-Docking denetim örneği (ADR 0129 §5; Excel "Software Workflow" 1, "Form
 * Field Schema" Inspection). Gemi verisidir: gemi sunucusu yazar, ofis salt okunur görür.
 */

export const DRYDOCK_PROJECT_KINDS = ['dry_docking', 'afloat_repair'] as const;
export type DrydockProjectKind = (typeof DRYDOCK_PROJECT_KINDS)[number];

/** Faz 1 yalnız `planning` ve `cancelled` yazar; tersane durumları Faz 4'te. */
export const DRYDOCK_PROJECT_STATES = ['planning', 'in_yard', 'completed', 'cancelled'] as const;
export type DrydockProjectState = (typeof DRYDOCK_PROJECT_STATES)[number];

export const PDI_INSPECTION_STATES = ['open', 'closed', 'cancelled'] as const;
export type PdiInspectionState = (typeof PDI_INSPECTION_STATES)[number];

export const drydockProjectSchema = z
  .strictObject({
    title: requiredText(200),
    kind: z.enum(DRYDOCK_PROJECT_KINDS, typed('INVALID_OPTION')),
    yardName: optionalText(200),
    yardLocation: optionalText(200),
    plannedStart: optionalDay,
    plannedEnd: optionalDay,
    /** İsteğe bağlı sörvey (`vessel_surveys`, aynı gemi). */
    surveyId: optionalUuid,
    notes: optionalParagraph(4000),
  })
  .superRefine((value, ctx) => {
    if (value.plannedStart && value.plannedEnd && value.plannedEnd < value.plannedStart) {
      ctx.addIssue({ code: 'custom', path: ['plannedEnd'], message: 'END_BEFORE_START' });
    }
  });
export type DrydockProjectInput = z.output<typeof drydockProjectSchema>;

export function parseDrydockProject(input: unknown): ParseResult<DrydockProjectInput> {
  return parsed(drydockProjectSchema.safeParse(input));
}

export const drydockCancelSchema = z.strictObject({ reason: requiredParagraph(1000) });

export function parseDrydockCancel(input: unknown): ParseResult<{ reason: string }> {
  return parsed(drydockCancelSchema.safeParse(input));
}

/** Proje numarası `DD-YYYY-NNN`: gemi ve yıl (gemi günü) başına sıra. */
export function drydockProjectNumber(localDay: string, lastSeq: number) {
  const year = Number(localDay.slice(0, 4));
  const seq = lastSeq + 1;
  return { number: `DD-${year}-${String(seq).padStart(3, '0')}`, year, seq };
}

/** Denetim örneğinin numarası: proje numarası + sıra (Excel "Project + sequence"). */
export function pdiInspectionNumber(projectNo: string, seq: number): string {
  return `${projectNo}/PDI-${seq}`;
}
